import { ActionIcon, Button, Tooltip } from '@mantine/core';
import { Icon, sizes, variants } from '@uag/react-core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/shallow';

import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { selectActiveInputProfile } from 'app/modules/wallforming/components/state-management/placement/slices/inputBindingSlice';

const EXPORT_FILE_NAME = 'wallforming-input-bindings.json';

function downloadJson(json: string): void {
    const url = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = EXPORT_FILE_NAME;
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(url));
}

interface InputBindingFooterProps {
    onClose: () => void;
}

export const InputBindingFooter = ({ onClose }: InputBindingFooterProps) => {
    const { t } = useTranslation();

    const { activeProfile, exportBindings, resetInputProfile } = usePlacementStore(
        useShallow((state) => ({
            activeProfile: selectActiveInputProfile(state),
            exportBindings: state.exportInputBindings,
            resetInputProfile: state.resetInputProfile
        }))
    );

    const isModified = useMemo(() => Object.keys(activeProfile.overrides).length > 0, [activeProfile.overrides]);

    return (
        <footer className="flex items-center gap-2 border-t border-border-ultra-light px-3 py-2">
            <div className="flex items-center gap-1">
                <Tooltip label={t('labelInputBindingExport')}>
                    <ActionIcon
                        aria-label={t('labelInputBindingExport')}
                        className="action-icon-small"
                        size="sm"
                        variant="subtle"
                        onClick={() => downloadJson(exportBindings())}
                    >
                        <Icon icon="file_download" />
                    </ActionIcon>
                </Tooltip>
                {activeProfile.readOnly && (
                    <Tooltip label={t('infoKeyBindingProfileReadOnly')} zIndex={20000}>
                        <span aria-hidden="true" className="flex items-center px-1">
                            <Icon className="icon-xs text-text-placeholder" icon="lock" />
                        </span>
                    </Tooltip>
                )}
            </div>
            <div className="ml-auto flex items-center gap-2">
                <Button
                    className="min-w-24 justify-center"
                    disabled={!isModified || activeProfile.readOnly}
                    size={sizes.button.small}
                    variant={variants.button.secondary}
                    onClick={resetInputProfile}
                >
                    {t('labelKeyBindingReset')}
                </Button>
                <Button className="min-w-24 justify-center" size={sizes.button.small} onClick={onClose}>
                    {t('ok')}
                </Button>
            </div>
        </footer>
    );
};
